import type { EnrollmentHistory, EnrollmentStatus } from '../types/enrollment';

/**
 * Rótulos de cabeçalho usados na exportação do histórico de status.
 */
export interface EnrollmentHistoryCsvLabels {
  readonly dateTime: string;
  readonly previousStatus: string;
  readonly newStatus: string;
  readonly reason: string;
  readonly subReason: string;
  readonly observation: string;
  readonly responsible: string;
}

/**
 * Escapa um valor para uma célula CSV, protegendo separador, aspas e quebras de linha.
 */
function escapeCsvCell(value: string): string {
  const needsQuoting = /[";\n\r]/.test(value);
  const escaped = value.replace(/"/g, '""');
  return needsQuoting ? `"${escaped}"` : escaped;
}

/**
 * Monta o conteúdo CSV do histórico de status de uma matrícula.
 *
 * Usa `;` como separador (compatível com Excel pt-BR) e os rótulos de status
 * traduzidos fornecidos via `statusLabel`.
 *
 * @param rows        - Transições de status (já ordenadas como devem aparecer)
 * @param labels      - Rótulos de cabeçalho traduzidos
 * @param statusLabel - Resolve o rótulo traduzido de um status de matrícula
 * @returns Conteúdo CSV pronto para download (sem BOM)
 */
export function buildEnrollmentHistoryCsv(
  rows: readonly EnrollmentHistory[],
  labels: EnrollmentHistoryCsvLabels,
  statusLabel: (status: EnrollmentStatus) => string,
): string {
  const header = [
    labels.dateTime,
    labels.previousStatus,
    labels.newStatus,
    labels.reason,
    labels.subReason,
    labels.observation,
    labels.responsible,
  ];

  const body = rows.map(row => [
    row.creationDate ? new Date(row.creationDate).toLocaleString('pt-BR') : '',
    row.oldStatus ? statusLabel(row.oldStatus) : '',
    statusLabel(row.newStatus),
    row.reasonName ?? '',
    row.subReasonName ?? '',
    row.observation ?? '',
    row.creationUser ?? '',
  ]);

  return [header, ...body].map(cells => cells.map(escapeCsvCell).join(';')).join('\r\n');
}
